import { useQuery } from '@tanstack/react-query';
import type { EventDTO, EventType, IndexerUsageView } from '@jelly-dl/shared';
import { api } from '../api/client';
import { Card, ErrorState, Spinner } from '../components/ui';
import { formatDateTime, timeAgo } from '../lib/format';

const TYPE_COLORS: Record<EventType, string> = {
  search: 'text-slate-400',
  grab: 'text-indigo-300',
  download_complete: 'text-cyan-300',
  download_failed: 'text-rose-300',
  import: 'text-emerald-300',
  import_failed: 'text-rose-300',
  upgrade: 'text-violet-300',
  indexer_limit: 'text-amber-300',
  downloader_paused: 'text-amber-300',
  downloader_resumed: 'text-cyan-300',
  error: 'text-rose-400',
  info: 'text-slate-400',
};

export function History() {
  const events = useQuery({
    queryKey: ['events'],
    queryFn: () => api.get<{ events: EventDTO[] }>('/api/events?limit=200'),
    refetchInterval: 8_000,
  });
  const usage = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.get<IndexerUsageView>('/api/usage'),
    refetchInterval: 10_000,
  });

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold">History</h1>

      {usage.data && (
        <Card className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <Stat label="API calls this hour" value={`${usage.data.localApiCount} / ${usage.data.hourlyApiCap}`} />
          <Stat
            label="Server API budget"
            value={usage.data.apiMax != null ? `${usage.data.apiCurrent ?? '?'} / ${usage.data.apiMax}` : '—'}
          />
          <Stat label="Grabs this hour" value={String(usage.data.localGrabCount)} />
          <Stat
            label="Backoff"
            value={
              usage.data.backoffUntil && usage.data.backoffUntil > Date.now()
                ? `until ${formatDateTime(usage.data.backoffUntil)}`
                : 'none'
            }
          />
        </Card>
      )}

      {events.isLoading && <Spinner />}
      {events.isError && <ErrorState message="Couldn't load history." onRetry={() => events.refetch()} />}
      <Card>
        <ul className="divide-y divide-slate-800 text-sm">
          {events.data?.events.map((e) => (
            <li key={e.id} className="flex items-start justify-between gap-4 py-2">
              <div>
                <span className={`mr-2 text-xs uppercase ${TYPE_COLORS[e.type] ?? 'text-slate-400'}`}>{e.type}</span>
                <span className="text-slate-200">{e.message}</span>
              </div>
              <span className="whitespace-nowrap text-xs text-slate-500">{timeAgo(e.at)}</span>
            </li>
          ))}
          {events.data && events.data.events.length === 0 && (
            <li className="py-3 text-slate-500">No events yet.</li>
          )}
        </ul>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-base font-medium text-slate-100">{value}</div>
    </div>
  );
}
